import { memo } from 'react';

import * as Sentry from '@sentry/react';
import { push } from 'connected-react-router';
import { withFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import * as paths from 'src/app/router/paths';
import { checkoutAddAddress } from 'src/app/router/paths';
import { accountLoadEverything, segmentTrack, validateAddressAction } from 'src/app/state/actions';
import { bffFetchCart, loadCartDeliveryMethods, updateCartDeliveryAddress } from 'src/app/state/actions/cart';
import { pushNextCheckoutState } from 'src/app/state/actions/checkout';
import * as actionTypes from 'src/app/state/actions/types';
import {
  createGetDeliveryAddressesSelector,
  getAccountIDVerificationStatus,
  getAccountIsFetching,
  getBFFCart,
  getBFFCartDeliveryAddress,
  getCurrentAccountId,
  getCurrentCartId,
  getCurrentCartUUID,
  getIsBffCartFetching,
  isDeliveryMethodsESIMailEligible,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { UpdateCartDeliveryAddressRequest } from 'src/app/types';
import { ESI_MAIL_IDENTIFY_KEY } from 'src/consts';

import validation from './validation';

import Renderer from '.';

function makeMapStateToProps() {
  const getDeliveryAddresses = createGetDeliveryAddressesSelector();

  return (state: RootState) => {
    const cartId = getCurrentCartId(state);
    const cartUUID = getCurrentCartUUID(state);
    const accountId = getCurrentAccountId(state);
    const accountIsFetching = getAccountIsFetching(state);
    const bffCartIsFetching = getIsBffCartFetching(state);
    const bffCart = getBFFCart(state);
    const idVerificationRequired = bffCart?.verifyId;
    const idVerificationStatus = getAccountIDVerificationStatus(state);
    const initialAddress = state?.setEditAddressState ?? null;
    const shouldEditAddress = state?.addressValidation?.userValidatedAddress ?? false;
    const fieldValues = state?.addressValidation?.invalidAddress ?? false;
    const deliveryAddresses = getDeliveryAddresses(state);
    const bffCartDeliveryAddressId = getBFFCartDeliveryAddress(state)?.id;

    return {
      accountId,
      bffCart,
      shouldEditAddress,
      initialAddress,
      cartId,
      cartUUID,
      fieldValues,
      hideLeaveWithDoorman:
        idVerificationStatus === null || idVerificationStatus === undefined ? idVerificationRequired : false,
      deliveryAddresses,
      bffCartDeliveryAddressId,
      accountIsFetching,
      bffCartIsFetching,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchLoadCart: cartId => dispatch(bffFetchCart(cartId)),
  dispatchLoadAccount: () => dispatch(accountLoadEverything()),
  dispatchValidateAddressAction: (values, dispatchUpdateCartAndSelectAddress, pushEditRoute) =>
    dispatch(validateAddressAction(values, dispatchUpdateCartAndSelectAddress, pushEditRoute, null)),
  dispatchPushCheckoutAddAddress: cartId => dispatch(push(checkoutAddAddress.toPath({ cartId }))),
  dispatchUpdateCartAndSelectAddress: async (cartId: any, deliveryAddress: { id: any }) => {
    await dispatch(updateCartDeliveryAddress({ cartUUID: cartId, deliveryAddressId: deliveryAddress?.id }));
    dispatch(push(paths.checkoutSelectAddress.toPath({ cartId })));
  },
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  dispatchUpdateCartAndPushNextCheckoutState: async (
    deliveryAddressBody: UpdateCartDeliveryAddressRequest,
    accountId: string
  ) => {
    await dispatch(updateCartDeliveryAddress(deliveryAddressBody));
    // Need to load delivery methods before pushing the next checkout state as product
    // wants us to skip the delivery method screen if ESI_MAIL is not an option
    const deliveryMethods = await dispatch(loadCartDeliveryMethods());
    const hasESIMailAsAnOption = isDeliveryMethodsESIMailEligible(deliveryMethods);

    dispatch({
      type: actionTypes.SEGMENT_IDENTIFY,
      accountId,
      traits: { [ESI_MAIL_IDENTIFY_KEY]: hasESIMailAsAnOption ? 1 : 0 },
    });

    await dispatch(pushNextCheckoutState());
  },
});

const connector = connect(makeMapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const enhancedWithFormik = withFormik<PropsFromRedux, typeof validation.defaultValues>({
  displayName: 'DeliveryAddressCreateForm',
  mapPropsToValues: props => {
    const { initialAddress, shouldEditAddress, fieldValues } = props;
    if (!isEmpty(initialAddress)) {
      return initialAddress;
    } else if (shouldEditAddress && fieldValues) {
      return { ...fieldValues };
    } else {
      return validation.defaultValues;
    }
  },
  validationSchema: validation.schema,
  validateOnChange: true,
  validateOnBlur: true,
  handleSubmit: async (values, { props, setErrors, setSubmitting }) => {
    const {
      dispatchValidateAddressAction,
      shouldEditAddress,
      dispatchPushCheckoutAddAddress,
      cartId,
      dispatchSegmentTrack,
      dispatchUpdateCartAndSelectAddress,
      initialAddress,
    } = props;
    try {
      setSubmitting(true);
      await dispatchValidateAddressAction(
        {
          ...values,
          ...(shouldEditAddress && { is_valid: false }),
        },
        deliveryAddress => {
          if (isEmpty(initialAddress)) {
            dispatchSegmentTrack(UNIFIED.addDeliveryAddress, {
              location: 'Checkout',
            });
          } else {
            dispatchSegmentTrack(UNIFIED.editAddressSuccessful, {
              location: 'Checkout',
            });
          }

          dispatchUpdateCartAndSelectAddress(cartId, deliveryAddress);
        },
        () => dispatchPushCheckoutAddAddress(cartId)
      );
    } catch (error) {
      setErrors(extractValidationErrors(error));
      Sentry.captureException(error);
      dispatchSegmentTrack(UNIFIED.editAddressFailed, {
        source: 'Checkout',
      });
      setSubmitting(false);
    }
  },
});

export default compose(connector, enhancedWithFormik, memo)(Renderer);
